import { normalize } from './text'
import { field, type SearchDoc } from './guideSearch'
import { marketTopics, topicPath } from '../data/market/topics'
import type { MarketTopic } from '../types/market'

/** Alias por grupo: se tratan como la misma palabra en la búsqueda local. */
const ALIAS_GROUPS: string[][] = [
  ['market', 'mercado', 'tienda'],
  ['wallet', 'billetera', 'saldo'],
  ['donacion', 'donar', 'recarga', 'recargar', 'deposito', 'depositar', 'cargar'],
  ['retiro', 'retirar', 'sacar', 'cobrar'],
  ['publicar', 'vender', 'venta', 'vendo'],
  ['custodia', 'resguardo', 'guardado', 'resguardado'],
  ['ficha', 'tooltip', 'opciones'],
  ['compro', 'comprar', 'compra'],
]

const STOP = new Set(['como', 'que', 'el', 'la', 'los', 'las', 'un', 'una', 'mi', 'mis', 'de', 'del', 'en', 'por', 'para', 'y', 'a', 'se', 'es', 'me', 'lo', 'al', 'donde', 'esta', 'cuanto', 'queda', 'pongo', 'hago', 'puedo', 'no'])

function expand(word: string): string[] {
  const g = ALIAS_GROUPS.find((grp) => grp.includes(word))
  return g ?? [word]
}

const haystack = (t: MarketTopic) =>
  normalize([t.title, t.menu, t.summary, ...t.aliases, ...t.questions, ...t.sections.map((s) => s.title)].join(' '))

export type MarketHit = { topic: MarketTopic; score: number }

/** Detecta "retirar" sin aclarar si es USDT o un objeto del Gremory. */
export function isAmbiguousWithdraw(query: string): boolean {
  const q = normalize(query)
  return /\bretir/.test(q) && !/\b(usdt|binance|creditos|saldo|dinero)\b/.test(q) && !/\b(item|items|objeto|gremory|deposito)\b/.test(q)
}

export function searchMarket(query: string): MarketHit[] {
  const q = normalize(query)
  const words = q.split(/[^a-z0-9]+/).filter((w) => w.length > 1 && !STOP.has(w))
  if (!words.length) return []
  const hits = marketTopics
    .map((topic) => {
      const text = haystack(topic)
      let score = 0
      words.forEach((w) => {
        const variants = expand(w)
        if (variants.some((v) => new RegExp(`\\b${v}`).test(text))) score += 2
      })
      if (topic.questions.some((qq) => normalize(qq) === q) || topic.aliases.some((a) => normalize(a) === q)) score += 6
      else if (topic.questions.some((qq) => normalize(qq).includes(q))) score += 3
      return { topic, score }
    })
    .filter((h) => h.score > 0)
    .sort((a, b) => b.score - a.score)
  if (isAmbiguousWithdraw(query)) {
    ;['user-withdraw', 'user-delivery'].forEach((key) => {
      const i = hits.findIndex((h) => h.topic.key === key)
      if (i > 1) hits.unshift(hits.splice(i, 1)[0])
    })
  }
  return hits.slice(0, 6)
}

/** Documentos del buscador global de la guía: un resultado por tema del Market. */
export function buildMarketDocs(): SearchDoc[] {
  return [
    {
      key: 'market-home',
      groupKey: 'market-home',
      group: 'Guías',
      title: 'Aprende a usar el Vanguard Market',
      typeLabel: 'Vanguard Market',
      context: 'Comprar, vender y hacer una donación paso a paso',
      path: '/vanguard-market',
      fields: [field('Vanguard Market', 10, 'Título'), ...['market', 'mercado', 'tienda web', 'marketplace'].map((a) => field(a, 9, 'Nombre alternativo'))],
    },
    ...marketTopics.map((t) => ({
      key: `market-${t.key}`,
      groupKey: `market-${t.key}`,
      group: 'Guías' as const,
      title: t.title,
      typeLabel: 'Vanguard Market',
      context: t.summary,
      path: topicPath(t),
      fields: [
        field(t.title, 9, 'Título'),
        field(`market ${t.menu}`, 8, 'Tema'),
        ...t.questions.map((q) => field(q, 9, 'Pregunta')),
        ...t.aliases.map((a) => field(`${a} market`, 6, 'Nombre alternativo')),
        field(t.summary, 3, 'Descripción'),
      ],
    })),
  ]
}
